import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { NotificationManager } from "react-notifications";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";
import { activityOptions, stateOptions } from "../../../utils/options";

/* ---------------- FIELD OPTIONS ---------------- */
const FIELD_OPTIONS = {
  "Property Name": ["Is", "Is Not", "Is Any", "Is All"],
  "Property Type": ["Is", "Is Not", "Is Any", "Is All", "Is Empty", "Is Not Empty"],
  "Property Tags": ["Is", "Is Not", "Is Any", "Is All", "Is Empty", "Is Not Empty"],
  "Street Address": ["Is", "Contains", "Is Empty", "Is Not Empty"],
  City: ["Is", "Contains", "Is Empty", "Is Not Empty"],
  State: ["Is", "Is Not", "Is Any", "Is All", "Is Empty", "Is Not Empty"],
  "Zip Code": ["Is", "Contains", "Starts With", "Is Empty", "Is Not Empty"],
  "Store #": ["Is", "Contains", "Is Empty", "Is Not Empty"],
  "Year Built": ["On", "Before", "After", "Between", "Is Empty", "Is Not Empty"],
  "Building Size": ["Is", "Contains", "Between", "Is Empty", "Is Not Empty"],
  "Land Size": ["Is", "Contains", "Between", "Is Empty", "Is Not Empty"],
  "Last Update Date": [
    "On",
    "Before",
    "After",
    "Between",
    "In Last X Days",
    "Longer Than X Days",
    "Is Empty",
    "Is Not Empty",
  ],
  "Asking Cap Rate": ["Is", "Less Than", "Greater Than", "Between", "Is Empty", "Is Not Empty"],
  "Asking Price": ["Is", "Less Than", "Greater Than", "Between", "Is Empty", "Is Not Empty"],
  "Last Sold Date": [
    "On",
    "Before",
    "After",
    "Between",
    "In Last X Days",
    "Longer Than X Days",
    "Is Empty",
    "Is Not Empty",
  ],
  "Sold Cap Rate": ["Is", "Less Than", "Greater Than", "Between", "Is Empty", "Is Not Empty"],
  "Sold Price": ["Is", "Less Than", "Greater Than", "Between", "Is Empty", "Is Not Empty"],
  "Owner (Company or Contact)": ["Is", "Contains", "Is Empty", "Is Not Empty"],
  "Owner Phone #": ["Is", "Contains", "Is Empty", "Is Not Empty"],
  "Owner Street Address": ["Is", "Contains", "Is Empty", "Is Not Empty"],
  "Owner Website": ["Is Empty", "Is Not Empty"],
  "Last Contacted": [
    "On",
    "Before",
    "After",
    "Between",
    "In Last X Days",
    "Longer Than X Days",
    "Is Empty",
    "Is Not Empty",
  ],
  User: ["Is", "Is Not", "Is Any", "Is All"],
  "Interaction Type": ["Is", "Is Not", "Is Any", "Is All"],
  "Tax Record Sent": [
    "On",
    "Before",
    "After",
    "Between",
    "In Last X Days",
    "Longer Than X Days",
    "Is Empty",
    "Is Not Empty",
  ],
  "Own X Amount of Properties": ["Is", "Less Than", "Greater Than", "Between", "Is Empty", "Is Not Empty"],
  "Owner Tags": ["Is", "Is Not", "Is Any", "Is All", "Is Empty", "Is Not Empty"],
};

/* --------- FIELDS THAT USE DROPDOWN --------- */
const DROPDOWN_FIELDS = {
  "Property Name": "properties_unique",
  "Property Type": "property_types",
  "Property Tags": "property_tag",
  "Owner (Company or Contact)": "owners",
  "Owner Tags": "owner_tags",
  User: "users",
  State: "states",
  "Interaction Type": "activity_types",
};

/* ---------------- JOIN OPTIONS ---------------- */
const JOIN_OPTIONS = [
  { label: "AND", value: "AND" },
  { label: "OR", value: "OR" },
];

/* 🔥 REQUIRED for react-select inside modal/sidebar */
const selectPortalStyles = {
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

const DATE_FIELDS = ["Last Update Date", "Last Sold Date", "Last Contacted", "Tax Record Sent"];
const NUMERIC_FIELDS = ["Year Built", "Own X Amount of Properties", "Asking Price", "Asking Cap Rate", "Sold Cap Rate", "Sold Price", "Building Size", "Land Size"];

const PREFIX_MAP = {
  "Year Built": "year_build_",
  "Last Update Date": "last_update_",
  "Last Sold Date": "last_sold_",
  "Last Contacted": "last_contact_",
  "Tax Record Sent": "tax_record_",
  "Own X Amount of Properties": "property_count_",
  "Asking Price": "asking_price_",
  "Asking Cap Rate": "asking_cap_rate_",
  "Sold Cap Rate": "sold_cap_rate_",
  "Sold Price": "sold_price_",
  "Building Size": "building_size_",
  "Land Size": "land_size_",
};

function AdvancePropertyFilterData({ filterData, onSetFilterData, isSidebarOpen }) {
  const [config] = useAuth();
  const [groups, setGroups] = useState(
    filterData && filterData.length > 0
      ? filterData
      : [
        {
          id: Date.now(),
          condition: "AND",
          filters: [{ join: null, field: "", operator: "", value: "" }],
        },
      ]
  );
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [showGroupSelector, setShowGroupSelector] = useState(false);

  // ✅ Initialize from parent data when sidebar opens
  useEffect(() => {
    if (isSidebarOpen && filterData && filterData.length > 0) {
      setGroups(filterData);

      // Pre-fetch dropdown options for existing fields
      filterData.forEach(group => {
        group.filters.forEach(filter => {
          if (filter.field && DROPDOWN_FIELDS[filter.field] && !dropdownOptions[filter.field]) {
            fetchDropdownOptions(filter.field).then((opts) =>
              setDropdownOptions((prev) => ({ ...prev, [filter.field]: opts }))
            );
          }
        });
      });
    }
  }, [isSidebarOpen]);

  /* ---------------- UPDATE STATE ---------------- */
  const updateState = (updated) => {
    console.log("🔥 Updating groups state:", updated); // DEBUG
    setGroups(updated);
    onSetFilterData(updated); // ✅ Always sync to parent
  };

  /* ---------------- API FETCHER ---------------- */
  const fetchDropdownOptions = async (field, search = "") => {
    const list = DROPDOWN_FIELDS[field];
    if (!list) return [];
    if (list === "states") {
      return stateOptions.filter((el) => el.label.toLowerCase().includes(search.toLowerCase()));
    } else if (list === "activity_types") {
      return activityOptions.filter((el) => el.label.toLowerCase().includes(search.toLowerCase()));
    } else {
      try {
        let options = [];
        if (list === "owners") {
          const resComp = await axios.get(
            `${BASE_URL}/get-datalisting?list=companies&search=${encodeURIComponent(search)}`,
            config
          );
          const resCont = await axios.get(
            `${BASE_URL}/get-datalisting?list=contacts&search=${encodeURIComponent(search)}`,
            config
          );
          const companies = resComp?.data?.data?.company_list || [];
          const contacts = resCont?.data?.data?.contact_list || [];
          options = companies.map((el) => ({
            label: `Company: ${el.company_name}`,
            value: `company_${el.id}`,
          })).concat(
            contacts.map((el) => ({
              label: `Contact: ${el.first_name} ${el.last_name}`,
              value: `contact_${el.id}`,
            }))
          );
        } else {
          const res = await axios.get(
            `${BASE_URL}/get-datalisting?list=${list}&search=${encodeURIComponent(search)}`,
            config
          );
          if (list === "properties_unique") {
            options =
              res?.data?.data?.properties?.map((el) => ({
                label: el.property_name,
                value: el.id,
              })) || [];
          }
          if (list === "property_types") {
            options =
              res?.data?.data?.property_types?.map((el) => ({
                label: el.type,
                value: el.id,
              })) || [];
          }
          if (list === "property_tag") {
            options =
              res?.data?.data?.property_tag?.map((el) => ({
                label: el.tag_name,
                value: el.id,
              })) || [];
          }
          if (list === "owner_tags") {
            options =
              res?.data?.data?.contact_tags?.map((el) => ({
                label: el.tag_name,
                value: el.id,
              })) || [];
          }
          if (list === "users") {
            options =
              res?.data?.data?.map((el) => ({
                label: `${el.first_name} ${el.last_name}`,
                value: el.id,
              })) || [];
          }
        }
        return options;
      } catch (err) {
        if (err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
        return [];
      }
    }
  };

  /* ---------------- ROW HANDLERS ---------------- */
  const addFilterRow = (groupIndex) => {
    const updated = [...groups];
    updated[groupIndex].filters.push({
      join: "AND",
      field: "",
      operator: "",
      value: "",
    });
    updateState(updated);
  };

  const updateRow = (groupIndex, filterIndex, key, value) => {
    const updated = [...groups];
    if (key === "field") {
      const newField = value?.value ?? value;
      updated[groupIndex].filters[filterIndex].field = newField;
      updated[groupIndex].filters[filterIndex].operator = "";
      const isDateField = DATE_FIELDS.includes(newField);
      const isNumericField = NUMERIC_FIELDS.includes(newField);
      if (isDateField || isNumericField) {
        const prefix = PREFIX_MAP[newField];
        updated[groupIndex].filters[filterIndex].value = {
          [`${prefix}count`]: "",
          [`${prefix}start_count`]: "",
          [`${prefix}end_count`]: "",
        };
      } else {
        updated[groupIndex].filters[filterIndex].value = "";
      }
      if (DROPDOWN_FIELDS[newField] && !dropdownOptions[newField]) {
        fetchDropdownOptions(newField).then((opts) =>
          setDropdownOptions((prev) => ({ ...prev, [newField]: opts }))
        );
      }
    } else if (key === "operator") {
      const newOp = value?.value ?? value;
      updated[groupIndex].filters[filterIndex].operator = newOp;
      const field = updated[groupIndex].filters[filterIndex].field;
      const isDateField = DATE_FIELDS.includes(field);
      const isNumericField = NUMERIC_FIELDS.includes(field);
      if (isDateField || isNumericField) {
        const prefix = PREFIX_MAP[field];
        // Reset value structure based on new operator
        if (newOp === "Between") {
          updated[groupIndex].filters[filterIndex].value = {
            [`${prefix}start_count`]: "",
            [`${prefix}end_count`]: "",
            [`${prefix}count`]: "",
          };
        } else if (["Is", "Less Than", "Greater Than"].includes(newOp)) {
          updated[groupIndex].filters[filterIndex].value = {
            [`${prefix}count`]: "",
            [`${prefix}start_count`]: "",
            [`${prefix}end_count`]: "",
          };
        } else {
          // Is Empty, Is Not Empty etc.
          updated[groupIndex].filters[filterIndex].value = "";
        }
      } else {
        const isMultiOp = ["Is Any", "Is All"].includes(newOp);
        updated[groupIndex].filters[filterIndex].value = isMultiOp ? [] : "";
      }
    } else {
      updated[groupIndex].filters[filterIndex][key] = value?.value ?? value;
    }
    updateState(updated);
  };

  const handleFilterChange = (groupIndex, filterIndex, subKey, subValue) => {
    const updated = [...groups];
    const filter = updated[groupIndex].filters[filterIndex];
    filter.value = { ...filter.value || {}, [subKey]: subValue };
    updateState(updated);
  };

  const removeRow = (groupIndex, filterIndex) => {
    const updated = [...groups];

    if (updated[groupIndex].filters.length === 1) {
      if (updated.length > 1) {
        updated.splice(groupIndex, 1);
      } else {
        updated[groupIndex].filters[0] = { join: null, field: "", operator: "", value: "" };
      }
    } else {
      updated[groupIndex].filters.splice(filterIndex, 1);
    }

    updateState(updated);
  };

  const createNestedGroup = (join) => {
    updateState([
      ...groups,
      {
        id: Date.now(),
        condition: join,
        filters: [{ join: null, field: "", operator: "", value: "" }],
      },
    ]);
    setShowGroupSelector(false);
  };

  /* ---------------- RENDER ---------------- */
  return (
    <div className="space-y-6 text-black">
      {groups.map((group, groupIndex) => (
        <div key={group.id} className="bg-white space-y-2">
          {groupIndex > 0 && (
            <div className="text-sm font-medium mb-2 text-gray-600">
              {group.condition} Group
            </div>
          )}
          {group.filters.map((filter, index) => (
            <div key={index}>
              {/* FILTER ROW - Field, Operator, Value on same line */}
              <div className="flex items-center gap-2">
                {/* JOIN SELECTOR */}
                {index > 0 && (
                  <Select
                    className="w-24 dark-H body-N"
                    placeholder="Join"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                    options={JOIN_OPTIONS}
                    value={
                      filter.join
                        ? { label: filter.join, value: filter.join }
                        : { label: "AND", value: "AND" }
                    }
                    onChange={(e) => updateRow(groupIndex, index, "join", e.value)}
                  />
                )}
                {/* FIELD */}
                <Select
                  className="flex-1 dark-H body-N"
                  placeholder="Select Field"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectPortalStyles}
                  options={Object.keys(FIELD_OPTIONS).map((f) => ({
                    label: f,
                    value: f,
                  }))}
                  value={
                    filter.field
                      ? { label: filter.field, value: filter.field }
                      : null
                  }
                  onChange={(e) => updateRow(groupIndex, index, "field", e)}
                />
                {/* OPERATOR */}
                <Select
                  className="flex-1 dark-H body-N"
                  placeholder="Condition"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectPortalStyles}
                  isDisabled={!filter.field}
                  options={
                    filter.field
                      ? FIELD_OPTIONS[filter.field].map((c) => ({
                        label: c,
                        value: c,
                      }))
                      : []
                  }
                  value={
                    filter.operator
                      ? { label: filter.operator, value: filter.operator }
                      : null
                  }
                  onChange={(e) => updateRow(groupIndex, index, "operator", e.value)}
                />
                {/* VALUE - Only for non-date/non-numeric fields */}
                {filter.operator && !["Is Empty", "Is Not Empty"].includes(filter.operator) &&
                  !DATE_FIELDS.includes(filter.field) && !NUMERIC_FIELDS.includes(filter.field) && (
                    DROPDOWN_FIELDS[filter.field] && filter.operator !== "Contains" ? (
                      (() => {
                        const isMulti = ["Is Any", "Is All"].includes(filter.operator);
                        const fieldOptions = dropdownOptions[filter.field] || [];
                        const selectedValue = filter.value
                          ? isMulti
                            ? fieldOptions.filter((opt) => filter.value.includes(opt.value))
                            : fieldOptions.find((opt) => opt.value === filter.value)
                          : isMulti
                            ? []
                            : null;
                        return (
                          <AsyncSelect
                            className="flex-1 dark-H body-N"
                            placeholder="Select Value"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={selectPortalStyles}
                            isMulti={isMulti}
                            defaultOptions={fieldOptions}
                            loadOptions={async (inputValue) => {
                              const opts = await fetchDropdownOptions(filter.field, inputValue);
                              setDropdownOptions((prev) => {
                                const existing = prev[filter.field] || [];
                                const uniqueOpts = [...existing, ...opts].reduce((acc, current) => {
                                  if (!acc.find((item) => item.value === current.value)) {
                                    acc.push(current);
                                  }
                                  return acc;
                                }, []);
                                return { ...prev, [filter.field]: uniqueOpts };
                              });
                              return opts;
                            }}
                            value={selectedValue}
                            onChange={(selected) => {
                              const newValue = isMulti
                                ? selected
                                  ? selected.map((opt) => opt.value)
                                  : []
                                : selected
                                  ? selected.value
                                  : null;
                              updateRow(groupIndex, index, "value", newValue);
                            }}
                          />
                        );
                      })()
                    ) : (
                      <input
                        className="flex-1 border px-2 py-1 rounded dark-H body-N"
                        placeholder="Enter Value"
                        value={filter.value || ""}
                        onChange={(e) =>
                          updateRow(groupIndex, index, "value", e.target.value)
                        }
                      />
                    )
                  )}
                {/* REMOVE BUTTON */}
                <button
                  className="text-red-500 hover:text-red-700 px-2"
                  onClick={() => removeRow(groupIndex, index)}
                  title="Remove filter"
                >
                  ✕
                </button>
              </div>

              {/* DATE RANGE */}
              {filter.operator &&
                !["Is Empty", "Is Not Empty"].includes(filter.operator) &&
                DATE_FIELDS.includes(filter.field) && (
                  <div className="mt-2 pl-2">
                    {(() => {
                      const prefix = PREFIX_MAP[filter.field];
                      const operator = filter.operator;
                      if (["In Last X Days", "Longer Than X Days"].includes(operator)) {
                        return (
                          <input
                            type="number"
                            className="dark-H body-N w-full"
                            placeholder="Enter number of days"
                            min={0}
                            value={filter.value?.[`${prefix}count`] || ""}
                            onChange={(e) => handleFilterChange(groupIndex, index, `${prefix}count`, e.target.value)}
                            onWheel={(e) => e.target.blur()}
                          />
                        );
                      } else if (["On", "Before", "After"].includes(operator)) {
                        return (
                          <DatePicker
                            className="dark-H body-N w-full"
                            dateFormat="MM/dd/yyyy"
                            placeholderText="MM/DD/YYYY"
                            maxDate={new Date()}
                            selected={filter.value?.[`${prefix}count`] ? moment.tz(filter.value?.[`${prefix}count`], 'America/New_York').toDate() : null}
                            onChange={(date) => {
                              const val = date ? moment.tz(date, 'America/New_York').format("YYYY-MM-DD") : "";
                              handleFilterChange(groupIndex, index, `${prefix}count`, val);
                            }}
                          />
                        );
                      } else if (operator === "Between") {
                        return (
                          <div className="flex flex-row gap-2">
                            <DatePicker
                              className="dark-H body-N"
                              dateFormat="MM/dd/yyyy"
                              placeholderText="Start Date (MM/DD/YYYY)"
                              maxDate={new Date()}
                              selected={filter.value?.[`${prefix}start_count`] ? moment.tz(filter.value?.[`${prefix}start_count`], 'America/New_York').toDate() : null}
                              onChange={(date) => {
                                const val = date ? moment.tz(date, 'America/New_York').format("YYYY-MM-DD") : "";
                                handleFilterChange(groupIndex, index, `${prefix}start_count`, val);
                              }}
                            />
                            <DatePicker
                              className="dark-H body-N"
                              dateFormat="MM/dd/yyyy"
                              placeholderText="End Date (MM/DD/YYYY)"
                              maxDate={new Date()}
                              selected={filter.value?.[`${prefix}end_count`] ? moment.tz(filter.value?.[`${prefix}end_count`], 'America/New_York').toDate() : null}
                              onChange={(date) => {
                                const val = date ? moment.tz(date, 'America/New_York').format("YYYY-MM-DD") : "";
                                handleFilterChange(groupIndex, index, `${prefix}end_count`, val);
                              }}
                            />
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}

              {/* NUMERIC INPUT */}
              {filter.operator &&
                !["Is Empty", "Is Not Empty"].includes(filter.operator) &&
                NUMERIC_FIELDS.includes(filter.field) && (
                  <div className="mt-2 pl-2">
                    {(() => {
                      const prefix = PREFIX_MAP[filter.field];
                      const operator = filter.operator;

                      // allow only numbers and commas
                      const sanitizeInput = (value) => {
                        return value.replace(/[^0-9,]/g, "");
                      };

                      if (operator === "Between") {
                        return (
                          <div className="flex flex-row gap-2">
                            <input
                              type="text"
                              className="dark-H body-N w-full"
                              placeholder="Min Value"
                              value={filter.value?.[`${prefix}start_count`] || ""}
                              onChange={(e) => {
                                const val = sanitizeInput(e.target.value);
                                handleFilterChange(
                                  groupIndex,
                                  index,
                                  `${prefix}start_count`,
                                  val
                                );
                              }}
                            />

                            <input
                              type="text"
                              className="dark-H body-N w-full"
                              placeholder="Max Value"
                              value={filter.value?.[`${prefix}end_count`] || ""}
                              onChange={(e) => {
                                const val = sanitizeInput(e.target.value);
                                handleFilterChange(
                                  groupIndex,
                                  index,
                                  `${prefix}end_count`,
                                  val
                                );
                              }}
                            />
                          </div>
                        );
                      }

                      return (
                        <input
                          type="text"
                          className="dark-H body-N w-full"
                          placeholder={
                            operator === "Is"
                              ? "Enter value"
                              : operator === "Less Than"
                                ? "Enter max value"
                                : operator === "Greater Than"
                                  ? "Enter min value"
                                  : "Enter value"
                          }
                          value={filter.value?.[`${prefix}count`] || ""}
                          onChange={(e) => {
                            const val = sanitizeInput(e.target.value);
                            handleFilterChange(groupIndex, index, `${prefix}count`, val);
                          }}
                        />
                      );
                    })()}
                  </div>
                )}
            </div>
          ))}
          {/* ADD FILTER BUTTON */}
          <button
            className="green-H head-5 mt-2"
            onClick={() => addFilterRow(groupIndex)}
          >
            + Add Filter
          </button>
        </div>
      ))}
      {/* ADD NESTED GROUP */}
      {!showGroupSelector ? (
        <button
          className="green-H head-5"
          onClick={() => setShowGroupSelector(true)}
        >
          + Add Nested Filter Group
        </button>
      ) : (
        <Select
          autoFocus
          className="dark-H body-N"
          placeholder="Select Group Type"
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={selectPortalStyles}
          options={[
            { label: "AND Group", value: "AND" },
            { label: "OR Group", value: "OR" },
          ]}
          onChange={(e) => createNestedGroup(e.value)}
          onBlur={() => setShowGroupSelector(false)}
        />
      )}
    </div>
  );
}
export default AdvancePropertyFilterData;